// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalFooter } from '@heroui/react';
import { Button } from '@/components/ui/button';
import { type ModalProps, useModal } from '@/stores/use-modal-store.ts';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { API_BASE } from '@/config.ts';
import { useAuth } from '@/context/auth-context/use-auth.ts';

export const LeaveServerModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const { server } = data;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const socket = useSocket();
  const { user } = useAuth();

  const onClick = async () => {
    try {
      setIsLoading(true);

      const res = await fetch(`${API_BASE}/api/servers/${server?.id}/leave`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      socket?.send(
        JSON.stringify({
          type: 'server-leave',
          payload: {
            id: server?.id,
            userId: user?.id
          },
        })
      );
      // TODO: translate
      toast.success('Вы покинули сервер');
      navigate('/discovery');

      onClose();
    } catch (error) {
      // TODO: translate
      toast.error('Something went wrong. Can not leave server');
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    onClose();
  };
  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">
                {t('LeaveServerModal.title')}
              </div>
              <div className="text-center">
                {t('LeaveServerModal.description')}{' '}
                <span className="font-semibold text-indigo-500">
                  {server?.name}
                </span>
                ?
              </div>
            </ModalHeader>
            <ModalFooter>
              <Button disabled={isLoading} onClick={onClose} variant="ghost">
                {t('LeaveServerModal.cancel')}
              </Button>
              <Button
                disabled={isLoading}
                color="primary"
                variant="primary"
                onClick={onClick}
              >
                {t('LeaveServerModal.confirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
